import React, { useEffect, useMemo, useState, useCallback } from 'react';
import AppLayout from '../components/AppLayout';

import { devicesApi, DeviceSummary, SharedDeviceEntry } from '../api/devices';
import { vpnApi, DeviceVPNInfo } from '../api/vpn';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useError } from '../contexts/ErrorContext';
import { formatDate } from '../utils/formatting';
import { extractErrorMessage } from '../utils/errorHandling';
import {
  ArrowPathIcon,
  ComputerDesktopIcon,
  ClipboardDocumentIcon,
  LinkIcon,
  LockClosedIcon,
  PlusIcon,
  ServerIcon,
  ShareIcon,
  WifiIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

type LoadingState = 'idle' | 'loading' | 'error';

const platformLabel: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  macos: 'macOS',
  windows: 'Windows',
  linux: 'Linux',
  router: 'Router',
  server: 'Server',
  other: 'Other',
};

const Devices: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const { addError } = useError();

  const [ownedDevices, setOwnedDevices] = useState<DeviceSummary[]>([]);
  const [sharedDevices, setSharedDevices] = useState<SharedDeviceEntry[]>([]);
  const [vpnDevices, setVpnDevices] = useState<DeviceVPNInfo[]>([]);
  const [togglingDevices, setTogglingDevices] = useState<Set<string>>(new Set());
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [isRegistering, setIsRegistering] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    name: '',
    description: '',
    platform: 'other',
  });
  const [pairingInfo, setPairingInfo] = useState<{
    device_id: string;
    pairing_code: string;
    expires_at?: string;
  } | null>(null);

  const refreshDevices = useCallback(async () => {
    try {
      setLoadingState('loading');
      const [owned, shared, vpnStatus] = await Promise.all([
        devicesApi.listOwned(),
        devicesApi.listShared(),
        vpnApi.getStatus(),
      ]);
      setOwnedDevices(owned.data.devices);
      setSharedDevices(shared.data.shared_devices);
      setVpnDevices(vpnStatus.data.devices || []);
      setLoadingState('idle');
    } catch (error) {
      console.error('Failed to load devices', error);
      setLoadingState('error');
      const apiError = extractErrorMessage(error, 'Failed to load devices');
      addError(apiError.message);
    }
  }, [addError]);

  useEffect(() => {
    refreshDevices();
  }, [refreshDevices]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!registerForm.name.trim()) {
      toast.error('Device name is required');
      return;
    }

    try {
      setIsRegistering(true);
      const response = await devicesApi.register({
        name: registerForm.name.trim(),
        description: registerForm.description.trim() || undefined,
        platform: registerForm.platform,
      });
      setPairingInfo(response.data);
      toast.success('Device registered. Pairing code generated.');
      setRegisterForm({ name: '', description: '', platform: registerForm.platform });
      await refreshDevices();
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to register device');
      addError(apiError.message);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleToggleHosting = async (deviceId: string, currentlyEnabled: boolean) => {
    try {
      setTogglingDevices((prev) => new Set(prev).add(deviceId));
      if (currentlyEnabled) {
        await vpnApi.disableSharing({ device_id: deviceId });
        toast.success('VPN hosting disabled for device');
      } else {
        await vpnApi.enableSharing({ device_id: deviceId });
        toast.success('VPN hosting enabled for device');
      }
      await refreshDevices();
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to toggle VPN hosting');
      addError(apiError.message);
    } finally {
      setTogglingDevices((prev) => {
        const next = new Set(prev);
        next.delete(deviceId);
        return next;
      });
    }
  };

  const getDeviceVpnInfo = useCallback((deviceId: string): DeviceVPNInfo | undefined => {
    return vpnDevices.find((vpnDevice) => vpnDevice.device_id === deviceId);
  }, [vpnDevices]);

  const statusBadge = (device: DeviceSummary) => {
    const isOnline = device.status === 'active';
    const className = isOnline
      ? 'bg-green-100 text-green-700'
      : device.status === 'pending'
      ? 'bg-yellow-100 text-yellow-700'
      : 'bg-gray-100 text-gray-700';
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
        <span
          className={`h-1.5 w-1.5 rounded-full mr-2 ${
            isOnline
              ? 'bg-green-500'
              : device.status === 'pending'
              ? 'bg-yellow-500'
              : 'bg-gray-500'
          }`}
        />
        {device.status}
      </span>
    );
  };

  const pairingExpires = useMemo(() => {
    if (!pairingInfo?.expires_at) return '30 minutes';
    return new Date(pairingInfo.expires_at).toLocaleString();
  }, [pairingInfo]);

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto py-10 px-4 space-y-10">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Device Mesh</h1>
        <p className="text-gray-600">Register your devices, monitor their status, and manage device sharing with friends.</p>
      </div>

      <section className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Register New Device</h2>
            <p className="text-sm text-gray-500">Generate a pairing code to link another device to your account.</p>
          </div>
        </div>

        <form onSubmit={handleRegister} className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700">Device name</label>
            <input
              type="text"
              value={registerForm.name}
              onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="e.g. MacBook Pro, Home Server"
              required
            />
          </div>
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700">Platform</label>
            <select
              value={registerForm.platform}
              onChange={(e) => setRegisterForm({ ...registerForm, platform: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              {Object.entries(platformLabel).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Description (optional)</label>
            <textarea
              value={registerForm.description}
              onChange={(e) => setRegisterForm({ ...registerForm, description: e.target.value })}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Describe where the device lives or what it should be used for"
            />
          </div>
          <div className="md:col-span-2 flex justify-end space-x-3">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
              onClick={() => setRegisterForm({ name: '', description: '', platform: registerForm.platform })}
            >
              <XMarkIcon className="-ml-1 mr-2 h-5 w-5 text-gray-400" aria-hidden="true" />
              Clear
            </button>
            <button
              type="submit"
              disabled={isRegistering}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              {isRegistering ? 'Registering...' : 'Generate Pairing Code'}
            </button>
          </div>
        </form>

        {pairingInfo && (
          <div className="mt-6 border border-primary-200 bg-primary-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-primary-900">
                  Pairing Code (expires {pairingExpires})
                </h3>
                <p className="mt-1 text-lg font-mono tracking-widest text-primary-800">
                  {pairingInfo.pairing_code}
                </p>
                <p className="mt-3 text-xs uppercase font-semibold text-primary-900">Device ID</p>
                <p className="text-sm font-mono text-primary-800 break-all">{pairingInfo.device_id}</p>
              </div>
              <button
                onClick={() => navigator.clipboard.writeText(pairingInfo.pairing_code)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-700 hover:text-primary-900"
              >
                <ClipboardDocumentIcon className="h-5 w-5 mr-2" /> Copy
              </button>
            </div>
            <p className="mt-2 text-sm text-primary-700">
              Use this code and device ID on the device you want to pair. You’ll receive a device token after pairing completes.
            </p>
          </div>
        )}
      </section>

      <section className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">My Devices ({ownedDevices.length})</h2>
            <p className="text-sm text-gray-500">Devices registered under {user?.display_name || 'your account'}.</p>
          </div>
          <button
            onClick={refreshDevices}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
          >
            <ArrowPathIcon className="-ml-1 mr-2 h-5 w-5 text-gray-400" aria-hidden="true" />
            Refresh
          </button>
        </div>

        {loadingState === 'error' && (
          <div className="rounded-md bg-red-50 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <LockClosedIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Failed to load devices.</h3>
                <p className="mt-1 text-sm text-red-700">Try refreshing the page. If the problem persists, contact support.</p>
              </div>
            </div>
          </div>
        )}

        {loadingState === 'loading' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className="border border-gray-100 rounded-lg p-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
                <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
                <div className="h-3 bg-gray-100 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ownedDevices.length === 0 ? (
              <p className="text-sm text-gray-500 col-span-full">
                You haven’t registered any devices yet. Use the form above to generate a pairing code.
              </p>
            ) : (
              ownedDevices.map((device) => {
                const vpnInfo = getDeviceVpnInfo(device.id);
                const isToggling = togglingDevices.has(device.id);
                return (
                  <div key={device.id} className="border border-gray-200 rounded-lg p-4 flex flex-col space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                          <span>{device.name}</span>
                          <span className="text-sm text-gray-400">({platformLabel[device.platform] || device.platform})</span>
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">Mesh IP: {device.mesh_ip || 'pending'}</p>
                        <p className="text-xs text-gray-400 mt-1 break-all">Device ID: {device.id}</p>
                      </div>
                      {statusBadge(device)}
                    </div>
                    {device.description && <p className="text-sm text-gray-600">{device.description}</p>}

                    {/* VPN Hosting Section */}
                    <div className="border-t border-gray-100 pt-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <ServerIcon className="h-5 w-5 text-purple-500" />
                          <span className="text-sm font-medium text-gray-700">VPN Hosting</span>
                        </div>
                        <button
                          onClick={() => handleToggleHosting(device.id, vpnInfo?.is_hosting_enabled || false)}
                          disabled={isToggling}
                          className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 ${
                            vpnInfo?.is_hosting_enabled
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {isToggling ? 'Updating...' : vpnInfo?.is_hosting_enabled ? 'Enabled' : 'Disabled'}
                        </button>
                      </div>
                      {vpnInfo && (
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                          <div>
                            <span className="font-medium">VPN IP:</span> {vpnInfo.vpn_ip || 'Not assigned'}
                          </div>
                          <div>
                            <span className="font-medium">Active Connections:</span> {vpnInfo.active_connections}
                          </div>
                          <div className="col-span-2">
                            <span className="font-medium">Keys:</span>{' '}
                            {vpnInfo.has_wireguard_keys ? (
                              <span className="text-green-600">Generated</span>
                            ) : (
                              <span className="text-gray-400">Not generated</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="inline-flex items-center">
                        <WifiIcon className="h-4 w-4 mr-1 text-primary-500" />
                        {device.can_route_traffic ? 'Can route traffic' : 'Routing disabled'}
                      </span>
                      <span className="inline-flex items-center">
                        <ShareIcon className="h-4 w-4 mr-1 text-primary-500" />
                        {device.can_accept_routes ? 'Accepts traffic' : 'Receiving disabled'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>Last seen: {formatDate(device.last_seen_at)}</span>
                      <span>Paired: {formatDate(device.paired_at)}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </section>

      <section className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Shared With Me ({sharedDevices.length})</h2>
            <p className="text-sm text-gray-500">Friends’ devices that you can use as exit nodes.</p>
          </div>
        </div>

        {sharedDevices.length === 0 ? (
          <p className="text-sm text-gray-500">No shared devices yet. Ask your friends to grant access from their device list.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sharedDevices.map((entry) => (
              <div key={entry.permission_id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                      <span>{entry.device.name}</span>
                      <span className="text-sm text-gray-400">({platformLabel[entry.device.platform] || entry.device.platform})</span>
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Owner ID: {entry.owner_id}</p>
                    <p className="text-xs text-gray-400 mt-1 break-all">Device ID: {entry.device.id}</p>
                  </div>
                  {statusBadge(entry.device)}
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span className="inline-flex items-center">
                    <ComputerDesktopIcon className="h-4 w-4 mr-1 text-purple-500" />
                    Mesh IP: {entry.device.mesh_ip || 'pending'}
                  </span>
                  <span className="inline-flex items-center">
                    <LinkIcon className="h-4 w-4 mr-1 text-sky-500" />
                    Status: {entry.status}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>Last seen: {formatDate(entry.device.last_seen_at)}</span>
                  <span>Paired: {formatDate(entry.device.paired_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      </div>
    </AppLayout>
  );
};

export default Devices;
